import React, { useState, useEffect, useRef } from "react";
import {
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Form,
  Row,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { debounce } from "lodash";
import WellSummaryService from "../../api-service/WellSummaryService";
import SessionHelper from "../../helpers/SessionHelper";
import StorageHelper from "../../helpers/StorageHelper";
import LoadingOverlay from "../common/LoadingOverlay";
import { PUMP_RATES_SUMMARY } from "../../configurations/Urls";
import {
  ERROR_DOWNLOADING_WELL_REPORT,
  INCOMING_TOKEN_HAS_EXPIRED,
  PUMP_RATES_REPORT_DESCRIPTION,
  ERROR_FETCHING_PUMP_RATES_REPORT,
} from "../../configurations/MessageConstants";
import { EMPTY, SESSION_EXPIRED } from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

const SearchBox = ({ onSearch, value }) => (
  <Form.Control
    type="text"
    defaultValue={value}
    name="searchValue"
    onChange={onSearch}
    placeholder="Search"
    className="form-control form-control-lg me-2 w-50"
  />
);

const Export = ({ onExport }) => (
  <DropdownButton
    id="dropdown-basic-button"
    title={
      <span>
        Export <i className="fa fa-download ms-2" aria-hidden="true"></i>
      </span>
    }
  >
    <Dropdown.Item onClick={() => onExport("pdf")}>Export as PDF</Dropdown.Item>
    <Dropdown.Item onClick={() => onExport("xlsx")}>
      Export as XLSX
    </Dropdown.Item>
  </DropdownButton>
);

function PumpRatesReport() {
  const [searchValue, setSearchValue] = useState(null);
  const [column, setColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportURL, setExportURL] = useState(EMPTY);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const abortControllerRef = useRef(null);

  const addGpmColumns = (maxGpm) => {
    const baseColumns = [
      {
        id: 1,
        name: "Field Name",
        selector: (row) => row.field_name,
        sortable: true,
        reorder: true,
        value: "field_name",
        grow: 2,
      },
      {
        id: 2,
        name: "Well ID",
        selector: (row) => row.well_id,
        sortable: true,
        reorder: true,
        value: "well_id",
        grow: 1,
      },
    ];

    const gpmColumns = [];
    for (let index = 1; index <= maxGpm; index++) {
      gpmColumns.push({
        id: 2 * index + 1,
        name: index === 1 ? "GPM" : `Prev GPM ${index - 1}`,
        selector: (row) => {
          const gpmValue = row[`gpm${index}`];
          return gpmValue === null || gpmValue === 0 ? "-" : gpmValue;
        },
        reorder: true,
        value: `gpm${index}`,
        grow: 1,
      });

      gpmColumns.push({
        id: 2 * index + 2,
        name: "Tested",
        selector: (row) => row[`tested${index}`] ?? "-",
        reorder: true,
        value: `tested${index}`,
        grow: 1,
      });
    }

    return [...baseColumns, ...gpmColumns];
  };

  useEffect(() => {
    setLoading(true);
    fetchPumpRateReports(
      currentPage,
      perPage,
      searchValue,
      column,
      columnOrder
    );
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentPage, perPage, searchValue, column, columnOrder]);

  const fetchPumpRateReports = async (
    page,
    size,
    searchValue,
    column,
    columnOrder
  ) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setLoading(true);
    let params = `?current_pg=${page}&rows_per_pg=${size}`;
    if (searchValue)
      params = `${params}&search_text=${encodeURIComponent(searchValue)}`;
    if (column !== null) params = `${params}&column=${column}`;
    if (columnOrder !== null) params = `${params}&column_order=${columnOrder}`;

    const url = `${PUMP_RATES_SUMMARY}${params}`;
    const exporturl = `${PUMP_RATES_SUMMARY}/export${params}`;

    try {
      const response = await WellSummaryService.getPumpRateReport(
        url,
        abortController.signal
      );
      let reportData = response.data.data;
      if (reportData) {
        setExportURL(exporturl);
        setData(reportData.data);
        setTotalRows(reportData.totalRows);
        const maxGpm = getMaxGpmCount(reportData.data);
        setColumns(addGpmColumns(maxGpm));
        setLoading(false);
      }
    } catch (error) {
      if (abortController.signal.aborted) return;
      setLoading(false);
      const responseData = error.response?.data;
      if (
        error.response?.status === StatusCodes.UNAUTHORIZED &&
        responseData?.message?.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
      console.error(ERROR_FETCHING_PUMP_RATES_REPORT, error);
    }
  };

  const getMaxGpmCount = (data) => {
    let maxGpmCount = 1;
    data.forEach((row) => {
      Object.keys(row).forEach((gpmKey) => {
        const match = gpmKey.match(/^gpm(\d+)$/);
        if (match) {
          const gpmCount = Number(match[1]);
          if (gpmCount > maxGpmCount) maxGpmCount = gpmCount;
        }
      });
    });
    return maxGpmCount;
  };

  const handleSearchValue = async (e) => {
    await debouncedSearch(e.target.value);
  };

  const debouncedSearch = debounce(async (value) => {
    setSearchValue(value);
  }, 400);

  const handleSort = async (column, sortDirection) => {
    setColumn(column.value);
    setColumnOrder(sortDirection);
  };

  const downloadCSV = async (url) => {
    setOverlayLoading(true);
    try {
      const response = await WellSummaryService.exportPumpRateReport(url);
      const filenameRegex = /filename="(.*)"/;
      const matches = filenameRegex.exec(
        response.headers["content-disposition"]
      );
      const fileName =
        matches && matches[1] ? matches[1] : "pump_rate_report.xlsx";
      let blob = await response.data;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      setOverlayLoading(false);
    } catch (error) {
      setOverlayLoading(false);
      const responseData = error.response?.data;
      if (
        error.response?.status === StatusCodes.UNAUTHORIZED &&
        responseData?.message?.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
      console.error(ERROR_DOWNLOADING_WELL_REPORT, error);
    }
  };

  const actions = (
    <div className="d-flex justify-content-end flex-grow-1 ms-0 field-well-crop-report-actions gap-2">
      <SearchBox onSearch={(e) => handleSearchValue(e, searchValue)} />
      <Export onExport={(item) => downloadCSV(`${exportURL}&type=${item}`)} />
    </div>
  );

  return (
    <div className="pump-rate-report">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text="Exporting Data..."
      />
      <div style={{ backgroundColor: "#e9ecef" }}>
        <div className="container mb-1">
          <Row>
            <Form.Group as={Col} className="mt-3 mb-1">
              <h2>Pump Rates</h2>
              <p className="text-muted">{PUMP_RATES_REPORT_DESCRIPTION}</p>
            </Form.Group>
          </Row>
        </div>
      </div>
      <Container className="p-3">
        <Row>
          <DataTable
            columns={columns}
            data={data}
            actions={actions}
            progressPending={loading}
            pagination
            paginationServer
            sortServer
            onSort={handleSort}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangeRowsPerPage={setPerPage}
            onChangePage={setCurrentPage}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Row>
      </Container>
    </div>
  );
}

export default PumpRatesReport;
